import { FormikProvider, useFormik } from 'formik';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Address } from '@woovi/form';
import { SwipeableModal, ActionButton } from '@woovi/ui';

type PaymentMethodCreditCardModalAddBillingAddressArgs = {
  onClose: () => void;
  open: () => void;
  setBillingAddress: () => void;
};

export type BillingAddressType = {
  address: {
    zipcode: string;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    complement: string;
  };
};

const PaymentMethodCreditCardAddBillingAddressModal = ({
  onClose,
  open,
  setBillingAddress,
}: PaymentMethodCreditCardModalAddBillingAddressArgs): ReactNode => {
  const { t } = useTranslation();

  const validationAddressSchema = yup.object().shape({
    address: yup.object().shape({
      zipcode: yup.string().required(t('Zipcode is required')),
      street: yup.string().required(t('Street is required')),
      number: yup.string().required(t('Number is required')),
      neighborhood: yup.string().required(t('Neighborhood is required')),
      city: yup.string().required(t('City is required')),
      state: yup.string().required(t('State is required')),
      complement: yup.string(),
    }),
  });

  const formikAddress = useFormik<BillingAddressType>({
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      address: {
        zipcode: '',
      },
    },
    validationSchema: validationAddressSchema,
    onSubmit: setBillingAddress,
  });

  const { isValid, handleSubmit } = formikAddress;

  const renderActions = (): ReactNode => {
    return (
      <>
        <ActionButton
          variant='text'
          color='secondary'
          data-testid={'btnCancelBillingAddress'}
          onClick={onClose}
        >
          {t('Cancel')}
        </ActionButton>
        <ActionButton
          color='primary'
          type='submit'
          disabled={!isValid}
          onClick={handleSubmit}
          data-testid={'btnConfirmBillingAddress'}
        >
          {t('Confirm address')}
        </ActionButton>
      </>
    );
  };

  return (
    <FormikProvider value={formikAddress}>
      <SwipeableModal
        title={t('Fill in the billing address')}
        open={open}
        onClose={onClose}
        actions={renderActions()}
        contentProps={{ sx: { zIndex: 400 } }}
      >
        <Address prefix='address' />
      </SwipeableModal>
    </FormikProvider>
  );
};

export default PaymentMethodCreditCardAddBillingAddressModal;
